import {
  AutocompleteChangeReason,
  AutocompleteProps,
  Box,
  List,
  ListProps,
} from '@mui/material';
import { debounce } from 'debounce';
import { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import SearchInput from 'components/SearchInput';
import { useHideTooltipsDuringScroll } from 'hooks';
import resolveInviteOption from './resolveInviteOption';
import { SearchBoxProps } from './interfaces';
import { SearchLabel, Autocomplete, RequireMark } from './SearchBox.styled';

function SearchList(props: ListProps) {
  useHideTooltipsDuringScroll('.searchBoxList');
  return <List classes={{ root: 'searchBoxList' }} {...props} />;
}

interface SearchBoxCustomProps<T> {
  noOptionsComponent?: any;
  noOptionsDisplayRule?: (a: string) => boolean;
  /**
   * Whether a result *is* what the user typed. Search matches on a substring,
   * so results can come back without any of them being the thing the user
   * asked for — `bob@x.com` matches an existing `alicebob@x.com`. Without this
   * test the component can only tell "nothing matched", which leaves the
   * wanted value unreachable.
   */
  isExactMatch?: (item: T, searchText: string) => boolean;
  onSearchChange?: (search: string) => void;
}

export default function SearchBox<T>({
  searchLabel,
  searchPlaceholder,
  searchThreshold = 3,
  isRequired = false,
  onSearch,
  onItemSelect,
  filterOptions = (options) => options,
  getOptionLabel,
  renderOption,
  isDisabled,
  sx,
  isSlim = true,
  noOptionsComponent,
  noOptionsDisplayRule,
  isExactMatch,
  onSearchChange,
}: SearchBoxProps<T> & SearchBoxCustomProps<T>) {
  const [searchText, setSearchText] = useState('');
  const [prevSearchText, setPrevSearchText] = useState('');
  const [items, setItems] = useState<T[]>([]);
  /** The text offered as a new entry, or null when nothing should be offered. */
  const [inviteOption, setInviteOption] = useState<string | null>(null);

  const clearSearch = useCallback(() => {
    setSearchText('');
    setPrevSearchText('');
    setItems([]);
    setInviteOption(null);
  }, []);

  // Takes the query rather than reading `searchText`, which the debounce can
  // leave a keystroke behind.
  const syncInviteOption = (data: T[], query: string) =>
    setInviteOption(
      resolveInviteOption({
        data,
        query,
        isOfferable: noOptionsDisplayRule,
        isExactMatch,
      }),
    );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const search = useCallback(
    debounce((query: string) => {
      if (!query) {
        setPrevSearchText('');
        clearSearch();
      } else if (query.length >= searchThreshold && query !== prevSearchText) {
        setPrevSearchText(query);
        onSearch(query).then(async (data) => {
          syncInviteOption(data, query);

          return setItems(data);
        });
      }
    }, 300),
    [onSearch, searchText],
  );

  useEffect(() => {
    clearSearch();
  }, [clearSearch]);

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(searchText);
    }

    search(searchText);
  }, [search, searchText, onSearchChange]);

  const onSearchTextChange = (event: SyntheticEvent, value: string) => {
    setSearchText(value);
  };

  const onOptionSelect = (
    event: SyntheticEvent<Element, Event>,
    value: unknown,
    reason: AutocompleteChangeReason,
  ) => {
    if (reason === 'selectOption') {
      onItemSelect(value as T);
    }
    setSearchText('');
    clearSearch();
  };

  // The invite entry sits alongside the matches rather than replacing them, so
  // a near-miss result never hides the value the user actually typed.
  const renderOptionFunc: AutocompleteProps<
    T,
    boolean,
    boolean,
    boolean
  >['renderOption'] = (props, option, state, ownerState) =>
    inviteOption !== null && (option as unknown) === inviteOption
      ? noOptionsComponent(props, option, state, ownerState)
      : renderOption?.(props, option, state, ownerState);

  const getItemsList = (): any[] =>
    inviteOption === null ? items : [...items, inviteOption];

  return (
    <Box sx={sx}>
      {searchLabel ? (
        <SearchLabel>
          {searchLabel}
          {isRequired && <RequireMark />}
        </SearchLabel>
      ) : null}
      <Autocomplete<T, boolean, boolean, boolean>
        freeSolo
        blurOnSelect={false}
        noOptionsText={null}
        fullWidth
        disabled={isDisabled}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        options={getItemsList()}
        inputValue={searchText}
        onInputChange={onSearchTextChange}
        value={null}
        onChange={onOptionSelect}
        ListboxComponent={SearchList}
        renderOption={renderOptionFunc}
        renderInput={(params) => (
          <SearchInput
            {...params}
            slim={isSlim}
            placeholder={searchPlaceholder}
            InputProps={{
              ...params.InputProps,
              placeholder: searchPlaceholder,
            }}
          />
        )}
        disablePortal={false}
      />
    </Box>
  );
}
